import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Calendar, MapPin, Loader2, XCircle, Ticket, Clock, CreditCard, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { bookingsApi, type BookingListResponse } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatNaira } from "@/data/venues";
import { toast } from "sonner";

export const Route = createFileRoute("/_user/bookings")({ component: MyBookingsPage });

const statusColor: Record<string, string> = {
  manager_approval_pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  checked_in: "bg-green-100 text-green-800",
  checked_out: "bg-gray-100 text-gray-800",
  cancelled: "bg-red-100 text-red-800",
  paid: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  pending_payment: "bg-yellow-100 text-yellow-800",
  expired: "bg-red-100 text-red-800",
};

export function MyBookingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<BookingListResponse[]>([]);
  const [eventBookings, setEventBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [payBooking, setPayBooking] = useState<any | null>(null);

  useEffect(() => {
    if (!user) return;
    
    // Load standard bookings
    bookingsApi.myBookings(user.id)
      .then((res) => setBookings(res.data))
      .catch(() => toast.error("Failed to load bookings"))
      .finally(() => setLoading(false));

    // Load event bookings
    const key = `oyo_event_bookings_${user.id}`;
    const local = localStorage.getItem(key);
    if (local) {
      try {
        setEventBookings(JSON.parse(local));
      } catch (e) {
        console.error(e);
      }
    }
  }, [user]);

  async function cancelBooking(id: string) {
    try { await bookingsApi.cancel(id); toast.success("Booking cancelled"); setBookings((b) => b.map((x) => x.id === id ? { ...x, status: "cancelled" } : x)); }
    catch (e: any) { toast.error(e?.message || "Cancel failed"); }
  }

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  const allBookings = [
    ...bookings.map((b) => ({
      ...b,
      isEvent: false,
      dateForSort: new Date(b.createdAt),
    })),
    ...eventBookings.map((eb) => {
      // Check if expired (30 minutes limit)
      const createdTime = new Date(eb.createdAt || eb.eventDate).getTime();
      const isExpired = eb.paymentStatus !== "paid" && (Date.now() - createdTime > 30 * 60 * 1000);
      const computedStatus = isExpired ? "expired" : (eb.paymentStatus === "paid" ? "confirmed" : "pending");
      const computedPaymentStatus = isExpired ? "expired" : eb.paymentStatus;

      return {
        id: eb.id,
        bookingRef: eb.ticketCode || "TICKET",
        venueName: eb.eventName,
        venueLocation: eb.eventLocation,
        startDate: eb.eventDate,
        endDate: eb.eventDate,
        totalAmount: eb.totalAmount,
        status: computedStatus,
        paymentStatus: computedPaymentStatus,
        isEvent: true,
        ticketCode: eb.ticketCode,
        quantity: eb.quantity,
        tierName: eb.tierName,
        createdAt: eb.createdAt || eb.eventDate,
        dedicatedBank: eb.dedicatedBank,
        paymentInstruction: eb.paymentInstruction,
        paymentDeadline: undefined,
        dateForSort: new Date(eb.createdAt || eb.eventDate),
      };
    }),
  ].sort((a, b) => b.dateForSort.getTime() - a.dateForSort.getTime());

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold">My Bookings</h1>
      {allBookings.length === 0 ? (
        <p className="text-muted-foreground">No bookings yet.</p>
      ) : (
        <div className="space-y-4">
          {allBookings.map((b) => {
            const isPendingVenue = !b.isEvent && b.paymentStatus !== "paid" && b.status !== "cancelled" && b.status !== "expired";
            return (
            <div key={b.id} className={`border rounded-lg p-4 bg-card flex flex-col md:flex-row md:items-center gap-4 ${isPendingVenue ? "cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all" : ""}`} onClick={isPendingVenue ? () => navigate({
              to: "/booking/success",
              search: {
                venue: b.venueName,
                ref: b.bookingRef,
                total: b.totalAmount,
                bookingId: b.id,
                paymentStatus: b.paymentStatus,
                bankName: "",
                accountName: "",
                accountNumber: "",
                paymentDeadline: b.paymentDeadline ?? "",
              },
            }) : undefined}>
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {b.isEvent && <Ticket className="h-4 w-4 text-primary shrink-0" />}
                  <span className="font-semibold">{b.venueName}</span>
                  <Badge className={statusColor[b.status] || "bg-gray-100"}>{b.status.replace(/_/g, " ")}</Badge>
                  {b.isEvent && <Badge variant="outline" className="text-xs">Event Ticket</Badge>}
                </div>
                <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> {b.venueLocation || b.venueName}</p>
                {b.isEvent ? (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {new Date(b.startDate).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {new Date(b.startDate).toLocaleDateString()} — {new Date(b.endDate).toLocaleDateString()}
                  </p>
                )}
                {b.isEvent && b.tierName && (
                  <p className="text-xs text-muted-foreground font-medium">
                    Ticket: {b.quantity}x {b.tierName} {b.ticketCode ? `(${b.ticketCode})` : ""}
                  </p>
                )}
                <p className="text-sm font-medium">{formatNaira(b.totalAmount)}</p>
              </div>
              <div className="flex flex-wrap gap-2" onClick={e => e.stopPropagation()}>
                {isPendingVenue && (
                  <Button
                    size="sm"
                    className="bg-amber-500 text-white hover:bg-amber-600 rounded-full"
                    onClick={() => navigate({
                      to: "/booking/success",
                      search: {
                        venue: b.venueName,
                        ref: b.bookingRef,
                        total: b.totalAmount,
                        bookingId: b.id,
                        paymentStatus: b.paymentStatus,
                        bankName: "",
                        accountName: "",
                        accountNumber: "",
                        paymentDeadline: b.paymentDeadline ?? "",
                      },
                    })}
                  >
                    <Clock className="h-3.5 w-3.5 mr-1" /> View Payment
                  </Button>
                )}
                {b.status !== "cancelled" && b.status !== "checked_out" && b.status !== "checked_in" && !b.isEvent && (
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => cancelBooking(b.id)}><XCircle className="h-3 w-3 mr-1" /> Cancel</Button>
                )}
                {b.isEvent && b.paymentStatus !== "paid" && b.paymentStatus !== "expired" && (
                  <Button
                    size="sm"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                    onClick={() => setPayBooking(b)}
                  >
                    <CreditCard className="h-3.5 w-3.5 mr-1" /> Proceed to Pay
                  </Button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {payBooking && (
        <CompletePaymentModal
          booking={payBooking}
          onClose={() => setPayBooking(null)}
        />
      )}
    </div>
  );
}

/* ── Countdown Timer ───────────────────────────────────────── */
function CountdownTimer({ createdAt, durationMinutes = 30 }: { createdAt: string; durationMinutes?: number }) {
  const computeRemaining = () => {
    const created = new Date(createdAt).getTime();
    const deadline = created + durationMinutes * 60 * 1000;
    return Math.max(0, deadline - Date.now());
  };

  const [remaining, setRemaining] = useState(computeRemaining);

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(computeRemaining());
    }, 1000);
    return () => clearInterval(interval);
  }, [createdAt, durationMinutes]);

  const mins = Math.floor(remaining / 60_000);
  const secs = Math.floor((remaining % 60_000) / 1000);
  const isExpired = remaining <= 0;

  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wide shadow-sm ${
      isExpired ? "bg-rose-50 text-rose-600 border-rose-200" : mins < 5 ? "bg-amber-50 text-amber-600 border-amber-200" : "bg-blue-50 text-blue-600 border-blue-200"
    }`}>
      <Clock className="size-2.5" />
      {isExpired ? "Expired" : `${mins}:${secs.toString().padStart(2, "0")} left`}
    </span>
  );
}

/* ── Complete Payment Modal ─────────────────────────────────── */
function CompletePaymentModal({ booking, onClose }: { booking: any; onClose: () => void }) {
  const eventBank = booking.dedicatedBank;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-md" onClick={onClose}>
      <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-primary px-6 py-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-2">
              <CreditCard className="size-4" /> Complete Event Payment
            </h2>
            <button onClick={onClose} className="rounded-full p-1 text-white/60 hover:text-white hover:bg-white/10 transition" aria-label="Close" title="Close">
              <X className="size-5" />
            </button>
          </div>
          <p className="mt-1 text-xs text-white/70">{booking.venueName}</p>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between rounded-xl bg-amber-50 border border-amber-200 p-3">
            <span className="text-xs font-semibold text-amber-700">Payment window</span>
            <CountdownTimer createdAt={booking.createdAt} durationMinutes={30} />
          </div>

          {eventBank ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 space-y-3">
              <p className="text-sm font-semibold text-emerald-800">Transfer to your dedicated account</p>
              <div className="grid gap-2 text-sm text-stone-800">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Bank</p>
                  <p className="font-medium">{eventBank.bankName}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Account Name</p>
                  <p className="font-medium">{eventBank.accountName}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Account Number</p>
                  <p className="font-mono font-medium">{eventBank.accountNumber}</p>
                </div>
              </div>
              <p className="text-xs text-emerald-700 font-medium">Transfer {formatNaira(booking.totalAmount)} to confirm your event ticket.</p>
              <Button onClick={() => { toast.info("We are verifying your transfer. Your ticket will remain pending until payment is confirmed."); onClose(); }} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">I Have Transferred</Button>
            </div>
          ) : (
            <div className="text-center py-6 text-stone-500 text-sm">
              No dedicated bank details found. Please contact support.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

